import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import { ConfigContext } from '../../shared/ConfigContext';
import { fetchFeatures, fetchPlan, updatePlan, planImpact, fetchPlanVersions } from '../../../api/planClient';
import { emptyDraftFromPlan, diffDraft, validateDraft, centsToInput, inputToCents, LIB_PREFIX, CANVAS_ID, LIBRARY_ID } from './planUtils';

const mapError = (t, err) => {
  if (err.response?.status === 401) return t('ga.error.unauthorized');
  if (err.response?.status === 403) return t('ga.error.forbidden');
  if (err.response?.status === 404) return t('ga.error.actionUnavailable');
  if (err.response?.status === 400 || err.response?.status === 409) return err.response?.data?.error || t('ga.error.generic');
  if (err.response?.status >= 500) return err.response?.data?.error || t('ga.error.server');
  if (!err.response) return t('ga.error.network');
  return t('ga.error.generic');
};

const useGAPlanBuilderPage = () => {
  const { t } = useTranslation();
  const { planId } = useParams();
  const config = useContext(ConfigContext);
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plan, setPlan] = useState(null);
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [priceInput, setPriceInputRaw] = useState('0.00');

  const [reviewOpen, setReviewOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(() => {
    if (!config?.backendUrl || !planId) return;
    setStatus('loading');
    Promise.all([fetchFeatures(config.backendUrl), fetchPlan(config.backendUrl, planId)])
      .then(([f, p]) => {
        setFeatures(f.data);
        setPlan(p.data);
        const d = emptyDraftFromPlan(p.data);
        setOriginal(d);
        setDraft(d);
        setPriceInputRaw(centsToInput(d.price.amount));
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error loading plan', error);
        if (error.response?.status === 401 || error.response?.status === 403) setStatus('permission');
        else {
          setErrorInfo({ request: `GET /ga/plans/${planId} · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}` });
          setStatus('error');
        }
      });
  }, [config, planId, t]);

  useEffect(() => { load(); }, [load]);

  const featuresByKey = useMemo(() => Object.fromEntries(features.map((f) => [f.key, f])), [features]);
  const changes = useMemo(() => diffDraft(original, draft, featuresByKey), [original, draft, featuresByKey]);
  const errors = useMemo(() => (draft ? validateDraft(draft, t, featuresByKey) : {}), [draft, t, featuresByKey]);
  const isDirty = changes.length > 0;
  const isValid = Object.keys(errors).length === 0;
  const inPlanKeys = useMemo(() => new Set((draft?.entitlements || []).map((e) => e.featureKey)), [draft]);

  // Warn on tab close while dirty (BrowserRouter has no useBlocker).
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const setPrice = (patch) => setDraft((d) => ({ ...d, price: { ...d.price, ...patch } }));
  const setPriceInput = (value) => { setPriceInputRaw(value); setPrice({ amount: inputToCents(value) }); };

  const addFeature = (featureKey) => {
    const feature = featuresByKey[featureKey];
    if (!feature || inPlanKeys.has(featureKey)) return;
    const stats = plan?.usageStats?.[featureKey];
    const limit = feature.kind === 'boolean' ? null : (stats?.p95 ? Math.max(stats.p95, 1) : 100);
    setDraft((d) => ({ ...d, entitlements: [...d.entitlements, { featureKey, enabled: true, limit }] }));
  };
  const removeFeature = (featureKey) => setDraft((d) => ({ ...d, entitlements: d.entitlements.filter((e) => e.featureKey !== featureKey) }));
  const changeEntitlement = (featureKey, next) => setDraft((d) => ({ ...d, entitlements: d.entitlements.map((e) => (e.featureKey === featureKey ? next : e)) }));

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId.startsWith(LIB_PREFIX)) {
      if (overId === LIBRARY_ID) return;
      addFeature(activeId.slice(LIB_PREFIX.length));
      return;
    }
    if (overId === LIBRARY_ID) { removeFeature(activeId); return; }
    if (overId !== CANVAS_ID && overId !== activeId && inPlanKeys.has(overId)) {
      setDraft((d) => {
        const keys = d.entitlements.map((e) => e.featureKey);
        return { ...d, entitlements: arrayMove(d.entitlements, keys.indexOf(activeId), keys.indexOf(overId)) };
      });
    }
  };

  const discard = () => { setDraft(original); setPriceInputRaw(centsToInput(original.price.amount)); };

  const openReview = async () => {
    if (!isDirty || !isValid) return;
    setReviewOpen(true);
    setImpact(null);
    setImpactLoading(true);
    try {
      const res = await planImpact(config.backendUrl, planId, draft);
      setImpact(res.data);
    } catch (error) {
      setReviewOpen(false);
      notify(mapError(t, error), 'error');
    } finally {
      setImpactLoading(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await updatePlan(config.backendUrl, planId, { ...draft, expectedVersion: plan.currentVersion });
      setReviewOpen(false);
      notify(t('Saved {{name}} as version {{version}}', { name: res.data.name, version: res.data.currentVersion, defaultValue: `Saved ${res.data.name} as version ${res.data.currentVersion}` }));
      load();
    } catch (error) {
      if (error.response?.status === 409) {
        setReviewOpen(false);
        notify(error.response?.data?.error || t('This plan was changed by someone else. Reloading.', { defaultValue: 'This plan was changed by someone else. Reloading.' }), 'warning');
        load();
      } else {
        notify(mapError(t, error), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const openVersions = async () => {
    setVersionsOpen(true);
    setVersionsLoading(true);
    try {
      const res = await fetchPlanVersions(config.backendUrl, planId);
      setVersions(res.data.items);
    } catch (error) {
      notify(mapError(t, error), 'error');
    } finally {
      setVersionsLoading(false);
    }
  };

  const restoreVersion = (version) => {
    const restored = emptyDraftFromPlan(version.snapshot);
    setDraft(restored);
    setPriceInputRaw(centsToInput(restored.price.amount));
    setVersionsOpen(false);
    notify(t('Version {{version}} loaded into the editor — review and save to apply', { version: version.version, defaultValue: `Version ${version.version} loaded into the editor — review and save to apply` }), 'info');
  };

  return {
    t, status, errorInfo, load, plan, features, featuresByKey, draft, original, changes, errors, isDirty, isValid, inPlanKeys,
    setField, setPrice, priceInput, setPriceInput, addFeature, removeFeature, changeEntitlement, handleDragEnd, discard,
    reviewOpen, setReviewOpen, openReview, impact, impactLoading, save, saving,
    versionsOpen, setVersionsOpen, openVersions, versions, versionsLoading, restoreVersion,
    goBack: () => navigate('/ga/plans'),
    snackbar, setSnackbar,
  };
};

export default useGAPlanBuilderPage;
